import { ControlValueAccessor, NgControl } from '@angular/forms';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  Self,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { MapsAPILoader } from '@ng-maps/core';
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter, from,
  Observable,
  of,
  repeat, startWith,
  Subject, Subscription, switchMap,
  take, tap,
} from 'rxjs';

import { Coords, Address } from './frontend-google-places.interfaces';
import { FrontendDropdownPlainComponent } from '@tablesready/frontend-dropdown';

@Component({
  selector: 'frontend-google-places',
  styleUrls: ['./frontend-google-places.component.scss'],
  templateUrl: './frontend-google-places.component.html',
})
export class FrontendGooglePlacesComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  @ViewChild(FrontendDropdownPlainComponent) dropdownRef?: FrontendDropdownPlainComponent;
  @Output() appliedCoords = new EventEmitter<Coords>();
  @Output() address = new EventEmitter<Address>();
  @Input() country = 'US';
  @Input() addressType?: string;
  @Input() placeholder?: string;
  @Input() validateAddress = true;
  @Input() initialValueValidated = false;

  options: google.maps.places.AutocompleteOptions = {};
  onChange: (value: string) => void;
  onTouched: () => void;
  disabled = false;

  value: string;
  places: string[] = [];

  private addressToPlaceIdMap: Record<string, string> = {};
  private placesService?: google.maps.places.PlacesService;
  private autocompleteService?: google.maps.places.AutocompleteService;
  private updatePlaces$ = new Subject<string>();
  private readonly subscription = new Subscription();

  constructor(
    @Self() public control: NgControl,
    private readonly mapsAPILoader: MapsAPILoader,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
  ) {
    control.valueAccessor = this;
  }

  onClickOutside(): void {
    if (this.value !== '') {
      return;
    }

    this.change(this.value);
  }

  searchTermChanged(term: string): void {
    this.updatePlaces$.next(term);
  }

  ngOnInit(): void {
    const placesSub = this.updatePlaces$.pipe(
      startWith(this.value),
      distinctUntilChanged(),
      concatMap((term) => {
        if (!term || term === '') {
          return of([]);
        }

        return this.searchPlaces(term, this.country);
      }),
      catchError(() => of([])),
      repeat(),
    )
      .subscribe((places: string[]) => {
        if (!this.initialValueValidated && !places.includes(this.value) && this.validateAddress) {
          this.value = null;
          this.reset();
          this.initialValueValidated = true;
          return;
        } else if (!this.initialValueValidated) {
          this.initialValueValidated = true;
        }

        if (places.length === 0) {
          this.dropdownRef?.resetItems();
          this.cdr.detectChanges();
        }

        this.places = places;
      });
    this.subscription.add(placesSub);
  }

  searchPlaces(
    searchString: string,
    country: string,
  ): Observable<string[]> {
    return new Observable((observer) => {
      from(this.mapsAPILoader.load())
        .pipe(
          tap(() => {
            if (!this.autocompleteService) {
              this.autocompleteService = new google.maps.places.AutocompleteService();
            }

            const request: google.maps.places.AutocompletionRequest = {
              input: searchString,
              componentRestrictions: country
                ? { country: country.toLowerCase() }
                : undefined,
              types: ['address'],
            };

            this.autocompleteService.getPlacePredictions(request, (predictions, status) => {
              this.ngZone.run(() => {
                if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                  observer.next([]);
                  observer.complete();
                  return;
                }

                if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
                  observer.error(`AutocompleteService failed: ${status}`);
                  return;
                }

                const formattedResults = predictions.map((prediction) => {
                  this.addressToPlaceIdMap[prediction.description] = prediction.place_id;
                  return prediction.description;
                });

                observer.next(formattedResults);
                observer.complete();
              });
            });
          }),
          catchError(err => of(err)),
        )
        .subscribe();
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['country'] && this.dropdownRef) {
      this.value = changes['country'].previousValue === null ? this.value : '';
      this.reset();
      this.updatePlaces$.next(this.value);
    }

    this.options = {
      types: [this.addressType || 'address'],
      componentRestrictions: { country: (this.country || '').toLowerCase() },
    };
    this.disabled = !this.country;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(value: string): void {
    this.value = value || '';
  }

  getPlaceDetails(placeId: string): Observable<google.maps.places.PlaceResult> {
    return new Observable((observer) => {
      this.mapsAPILoader.load().then(() => {
        if (!this.placesService) {
          const dummyMap = document.createElement('div');
          this.placesService = new google.maps.places.PlacesService(dummyMap);
        }

        const request: google.maps.places.PlaceDetailsRequest = { placeId };
        this.placesService.getDetails(request, (result, status) => {
          this.ngZone.run(() => {
            if (status === google.maps.places.PlacesServiceStatus.OK && result) {
              observer.next(result);
              observer.complete();
            } else {
              observer.error(`getDetails() failed due to: ${status}`);
            }
          });
        });
      });
    });
  }

  placeResult(address: string): void {
    this.change(address);

    if (!this.addressToPlaceIdMap[address]) {
      return;
    }

    this.getPlaceDetails(this.addressToPlaceIdMap[address])
      .pipe(take(1))
      .subscribe((place) => {
        if (!place) {
          return;
        }

        if (place.geometry) {
          this.appliedCoords.emit({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        }

        if (place.address_components) {
          this.address.emit(this.convertEventToAddress(place.address_components));
        }
      });
  }

  change(value: string) {
    if (this.onChange) {
      this.onChange(value);
    }
    if (this.onTouched) {
      this.onTouched();
    }
  }

  private reset(): void {
    this.dropdownRef?.resetItems();
    this.change(this.value);
    this.cdr.detectChanges();
  }

  private convertEventToAddress(data: google.maps.GeocoderAddressComponent[]): Address {
    const address: Address = {};
    for (const { long_name: longName, types } of data) {
      if (types.includes('locality')) {
        address.city = longName;
        continue;
      }

      if (types.includes('administrative_area_level_1')) {
        address.state = longName;
        continue;
      }

      if (types.includes('postal_code')) {
        address.postalCode = longName;
      }
    }

    return address;
  }
}
